import * as React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Card, CardContent, Typography, Grid, Box, Button,
    CircularProgress, Alert, IconButton, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, Paper, Breadcrumbs, Link, Skeleton,
    TextField, FormControl, InputLabel, Select, MenuItem,
    Accordion, AccordionSummary, AccordionDetails, Chip,
    Badge, Tabs, Tab, Tooltip
} from '@mui/material';
import { useNotify } from 'react-admin';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StorageIcon from '@mui/icons-material/Storage';
import SettingsIcon from '@mui/icons-material/Settings';
import DataObjectIcon from '@mui/icons-material/DataObject';
import InfoIcon from '@mui/icons-material/Info';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import TableViewIcon from '@mui/icons-material/TableView';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

export const DynamicProcPage = () => {
    const navigate = useNavigate();
    const notify = useNotify();
    const [searchParams, setSearchParams] = useSearchParams();

    // Stati
    const [procedures, setProcedures] = React.useState([]);
    const [selectedProc, setSelectedProc] = React.useState('');
    const [procConfig, setProcConfig] = React.useState(null);
    const [parameters, setParameters] = React.useState({});
    const [result, setResult] = React.useState(null);
    const [loading, setLoading] = React.useState(false);
    const [loadingProcs, setLoadingProcs] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [copiedJson, setCopiedJson] = React.useState(false);
    const [copiedUrl, setCopiedUrl] = React.useState(false);
    const [selectedDataTab, setSelectedDataTab] = React.useState(0);

    // Carica la lista delle procedure disponibili
    const loadProcedures = async () => {
        try {
            setLoadingProcs(true);
            const jwt = getJwt();

            if (!jwt) {
                notify("Token di autenticazione non trovato", { type: 'error' });
                return;
            }

            const response = await axios.get(
                `${host}/proc-configurations`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        is_active: 1,
                        $select: ['proc_name', 'description', 'result_type', 'has_temp_table'],
                        $sort: { proc_name: 1 }
                    }
                }
            );

            setProcedures(response.data.data || []);

            // Se c'è una procedura nel query param, selezionala
            const procFromUrl = searchParams.get('proc_name');
            if (procFromUrl && response.data.data.some(p => p.proc_name === procFromUrl)) {
                setSelectedProc(procFromUrl);
                await loadProcConfig(procFromUrl);
            }

        } catch (error) {
            console.error("Errore nel caricamento procedure:", error);
            const errorMessage = error.response?.data?.message || "Errore nel caricamento delle procedure";
            notify(errorMessage, { type: 'error' });
            setError(errorMessage);
        } finally {
            setLoadingProcs(false);
        }
    };

    // Copia il JSON dei risultati
    const copyJsonToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(result, null, 2));
            setCopiedJson(true);
            notify("JSON copiato negli appunti", { type: 'success' });

            // Reset del feedback dopo 2 secondi
            setTimeout(() => {
                setCopiedJson(false);
            }, 2000);
        } catch (error) {
            console.error("Errore nella copia:", error);
            notify("Errore nella copia del JSON", { type: 'error' });
        }
    };

    // Copia l'URL della chiamata
    const copyUrlToClipboard = async () => {
        try {
            const currentUrl = buildApiUrl();
            await navigator.clipboard.writeText(currentUrl);
            setCopiedUrl(true);
            notify("URL copiato negli appunti", { type: 'success' });

            // Reset del feedback dopo 2 secondi
            setTimeout(() => {
                setCopiedUrl(false);
            }, 2000);
        } catch (error) {
            console.error("Errore nella copia URL:", error);
            notify("Errore nella copia dell'URL", { type: 'error' });
        }
    };

    // Costruisce l'URL completo dell'API
    const buildApiUrl = () => {
        if (!selectedProc) return '';

        const queryParams = {
            proc_name: selectedProc,
            ...parameters
        };

        // Rimuovi parametri vuoti ma mantieni gli zeri e i false
        Object.keys(queryParams).forEach(key => {
            if (queryParams[key] === '' || queryParams[key] === null || queryParams[key] === undefined) {
                delete queryParams[key];
            }
        });

        const urlParams = new URLSearchParams();
        Object.entries(queryParams).forEach(([key, value]) => {
            urlParams.append(key, String(value));
        });

        return `${host}/dynamic-proc?${urlParams.toString()}`;
    };

    // Carica la configurazione di una procedura specifica
    const loadProcConfig = async (procName) => {
        try {
            const jwt = getJwt();

            if (!jwt) {
                notify("Token di autenticazione non trovato", { type: 'error' });
                return;
            }

            const response = await axios.get(
                `${host}/proc-configurations`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        proc_name: procName,
                        $limit: 1
                    }
                }
            );

            if (response.data.data && response.data.data.length > 0) {
                const config = response.data.data[0];
                setProcConfig(config);

                // Inizializza i parametri con i valori di default
                const defaultParams = {};
                if (config.input_params && Array.isArray(config.input_params)) {
                    config.input_params.forEach(param => {
                        defaultParams[param.name] = param.default_value !== undefined ? param.default_value : '';
                    });
                }
                setParameters(defaultParams);

                // Aggiorna URL
                setSearchParams({ proc_name: procName });
            } else {
                notify(`Configurazione per la procedura '${procName}' non trovata`, { type: 'error' });
                setSelectedProc('');
                setProcConfig(null);
            }

        } catch (error) {
            console.error("Errore nel caricamento configurazione:", error);
            const errorMessage = error.response?.data?.message || "Errore nel caricamento configurazione";
            notify(errorMessage, { type: 'error' });
        }
    };

    // Gestisce la selezione della procedura
    const handleProcChange = (event) => {
        const procName = event.target.value;
        setSelectedProc(procName);
        setProcConfig(null);
        setParameters({});
        setResult(null);
        setError(null);
        setSelectedDataTab(0); // Reset tab dati

        if (procName) {
            loadProcConfig(procName);
        } else {
            setSearchParams({});
        }
    };

    // Gestisce il cambio dei parametri
    const handleParameterChange = (paramName, value) => {
        setParameters(prev => ({
            ...prev,
            [paramName]: value
        }));
    };

    // Validazione parametri prima dell'esecuzione
    const validateParameters = () => {
        if (!procConfig || !procConfig.input_params) return true;

        const errors = [];

        procConfig.input_params.forEach(param => {
            const value = parameters[param.name];

            // Controllo parametri obbligatori
            if (param.required && (value === '' || value === null || value === undefined)) {
                errors.push(`Il parametro '${param.name}' è obbligatorio`);
            }

            // Validazione tipo numerico
            if (value !== '' && value !== null && value !== undefined) {
                if (['int', 'integer'].includes(param.type?.toLowerCase())) {
                    const numValue = parseInt(value, 10);
                    if (isNaN(numValue)) {
                        errors.push(`Il parametro '${param.name}' deve essere un numero intero`);
                    }
                } else if (['float', 'decimal', 'double'].includes(param.type?.toLowerCase())) {
                    const numValue = parseFloat(value);
                    if (isNaN(numValue)) {
                        errors.push(`Il parametro '${param.name}' deve essere un numero decimale`);
                    }
                }
            }
        });

        if (errors.length > 0) {
            notify(errors.join(', '), { type: 'error' });
            return false;
        }

        return true;
    };

    // Esegue la procedura
    const executeProcedure = async () => {
        if (!selectedProc) {
            notify("Seleziona una procedura", { type: 'warning' });
            return;
        }

        // Validazione parametri
        if (!validateParameters()) {
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            if (!jwt) {
                notify("Token di autenticazione non trovato", { type: 'error' });
                return;
            }

            // Prepara i parametri per la chiamata
            const queryParams = {
                proc_name: selectedProc,
                ...parameters
            };

            // Rimuovi parametri vuoti ma mantieni gli zeri e i false
            Object.keys(queryParams).forEach(key => {
                if (queryParams[key] === '' || queryParams[key] === null || queryParams[key] === undefined) {
                    delete queryParams[key];
                }
            });

            console.log('Parametri di chiamata:', queryParams);

            const response = await axios.get(
                `${host}/dynamic-proc`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: queryParams,
                    timeout: 30000 // Timeout di 30 secondi per procedure complesse
                }
            );

            setResult(response.data);
            setSelectedDataTab(0); // Reset alla prima tab
            notify("Procedura eseguita con successo", { type: 'success' });

        } catch (error) {
            console.error("Errore nell'esecuzione della procedura:", error);

            let errorMessage = "Errore nell'esecuzione della procedura";

            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.code === 'ECONNABORTED') {
                errorMessage = "Timeout: la procedura ha impiegato troppo tempo per rispondere";
            } else if (error.code === 'NETWORK_ERROR') {
                errorMessage = "Errore di connessione al server";
            }

            setError(errorMessage);
            notify(errorMessage, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Carica le procedure al mount
    React.useEffect(() => {
        loadProcedures();
    }, []);

    // Funzione per formattare il tipo di dato
    const formatDataType = (type) => {
        const typeMap = {
            'int': 'Numero intero',
            'integer': 'Numero intero',
            'float': 'Numero decimale',
            'decimal': 'Numero decimale',
            'double': 'Numero decimale',
            'varchar': 'Testo',
            'text': 'Testo lungo',
            'string': 'Testo',
            'datetime': 'Data e ora',
            'timestamp': 'Data e ora',
            'boolean': 'Vero/Falso',
            'bool': 'Vero/Falso'
        };
        return typeMap[type?.toLowerCase()] || type;
    };

    // Render del campo input in base al tipo
    const renderInputField = (param) => {
        const { name, type, required, description } = param;
        const value = parameters[name] !== undefined ? parameters[name] : '';

        switch (type?.toLowerCase()) {
            case 'datetime':
            case 'timestamp':
                return (
                    <TextField
                        fullWidth
                        type="datetime-local"
                        label={name}
                        value={value}
                        onChange={(e) => handleParameterChange(name, e.target.value)}
                        required={required}
                        helperText={description}
                        size="small"
                        InputLabelProps={{
                            shrink: true,
                        }}
                    />
                );

            case 'boolean':
            case 'bool':
                return (
                    <FormControl fullWidth size="small">
                        <InputLabel>{name}</InputLabel>
                        <Select
                            value={value}
                            onChange={(e) => handleParameterChange(name, e.target.value)}
                            label={name}
                        >
                            <MenuItem value="">Non specificato</MenuItem>
                            <MenuItem value={true}>Vero</MenuItem>
                            <MenuItem value={false}>Falso</MenuItem>
                        </Select>
                        {description && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                {description}
                            </Typography>
                        )}
                    </FormControl>
                );

            default:
                return (
                    <TextField
                        fullWidth
                        type={['int', 'integer', 'float', 'decimal', 'double'].includes(type?.toLowerCase()) ? 'number' : 'text'}
                        label={name}
                        value={value}
                        onChange={(e) => handleParameterChange(name, e.target.value)}
                        required={required}
                        helperText={description}
                        size="small"
                        inputProps={{
                            step: ['float', 'decimal', 'double'].includes(type?.toLowerCase()) ? 'any' : undefined
                        }}
                    />
                );
        }
    };

    // Funzione per determinare se i dati sono multiple tabelle temporanee
    const isMultipleTemporaryTables = (data) => {
        return data && typeof data === 'object' && !Array.isArray(data) &&
            Object.keys(data).some(key => key.startsWith('tmp_') || key.startsWith('temp_'));
    };

    // Funzione per renderizzare una singola tabella
    const renderSingleTable = (data, title = "Dati") => {
        if (!data || data.length === 0) {
            return (
                <Alert severity="info" icon={<InfoIcon />}>
                    Nessun record trovato nella tabella {title}.
                </Alert>
            );
        }

        const columns = Object.keys(data[0]);

        return (
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column} sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                                    <Tooltip title={`Colonna: ${column}`} arrow>
                                        <span>{column}</span>
                                    </Tooltip>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row, index) => (
                            <TableRow key={index} hover>
                                {columns.map((column, colIndex) => {
                                    const cellValue = row[column];
                                    return (
                                        <TableCell key={colIndex}>
                                            {cellValue === null ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    NULL
                                                </Typography>
                                            ) : cellValue === undefined ? (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                    UNDEFINED
                                                </Typography>
                                            ) : (
                                                <Tooltip title={String(cellValue)} arrow>
                                                    <span style={{
                                                        maxWidth: '200px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        display: 'block',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {String(cellValue)}
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    // Funzione per renderizzare i dati (singola tabella o multiple tabelle temporanee)
    const renderResultData = () => {
        if (!result.data) return null;

        // Se è un array (singola tabella)
        if (Array.isArray(result.data)) {
            if (result.data.length === 0) {
                return (
                    <Alert severity="info" icon={<InfoIcon />}>
                        Nessun record trovato con i parametri specificati.
                    </Alert>
                );
            }
            return renderSingleTable(result.data);
        }

        // Se è un oggetto con multiple tabelle temporanee
        if (isMultipleTemporaryTables(result.data)) {
            const tableNames = Object.keys(result.data).filter(key =>
                key.startsWith('tmp_') || key.startsWith('temp_')
            );

            if (tableNames.length === 0) {
                return (
                    <Alert severity="warning" icon={<WarningIcon />}>
                        Nessuna tabella temporanea trovata nei risultati.
                    </Alert>
                );
            }

            return (
                <Box>
                    {/* Tabs per le diverse tabelle */}
                    <Tabs
                        value={selectedDataTab}
                        onChange={(e, newValue) => setSelectedDataTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                    >
                        {tableNames.map((tableName, index) => (
                            <Tab
                                key={tableName}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TableViewIcon fontSize="small" />
                                        <span>{tableName.replace(/^(tmp_|temp_)/, '').replace(/_/g, ' ')}</span>
                                        <Badge
                                            badgeContent={result.data[tableName]?.length || 0}
                                            color="primary"
                                            max={9999}
                                        />
                                    </Box>
                                }
                                sx={{ textTransform: 'none' }}
                            />
                        ))}
                    </Tabs>

                    {/* Contenuto della tab selezionata */}
                    {tableNames.map((tableName, index) => (
                        <Box
                            key={tableName}
                            role="tabpanel"
                            hidden={selectedDataTab !== index}
                        >
                            {selectedDataTab === index && (
                                <Box>
                                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                            {tableName}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Record trovati: {result.data[tableName]?.length || 0}
                                        </Typography>
                                    </Box>
                                    {renderSingleTable(result.data[tableName], tableName)}
                                </Box>
                            )}
                        </Box>
                    ))}
                </Box>
            );
        }

        // Fallback per altri tipi di dati
        return (
            <Alert severity="warning" icon={<WarningIcon />}>
                Formato dati non riconosciuto. Controlla la struttura della risposta.
                <pre style={{ marginTop: '8px', fontSize: '0.8em' }}>
                    {JSON.stringify(result.data, null, 2)}
                </pre>
            </Alert>
        );
    };

    // Funzione per ottenere l'icona del tipo di procedura
    const getProcedureTypeIcon = (proc) => {
        if (proc.result_type === 'with_output_params') {
            return <DataObjectIcon fontSize="small" color="primary" />;
        } else if (proc.has_temp_table) {
            return <TableViewIcon fontSize="small" color="secondary" />;
        } else {
            return <StorageIcon fontSize="small" color="action" />;
        }
    };

    // Loading iniziale
    if (loadingProcs) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}>
                    <Box sx={{ mb: 3 }}>
                        <Skeleton variant="text" width={300} height={24} />
                    </Box>
                    <Card sx={{ p: 3 }}>
                        <Skeleton variant="text" width={200} height={32} />
                        <Skeleton variant="rectangular" width="100%" height={56} sx={{ mt: 2 }} />
                    </Card>
                </motion.div>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f6f7fb', minHeight: '100vh' }}>
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
            >
                {/* Breadcrumb */}
                <Breadcrumbs sx={{ mb: 3 }}>
                    <Link
                        component="button"
                        variant="body2"
                        onClick={() => navigate('/')}
                        sx={{ textDecoration: 'none' }}
                    >
                        Dashboard
                    </Link>
                    <Typography variant="body2" color="text.primary">
                        Dynamic Procedures
                    </Typography>
                </Breadcrumbs>

                {/* Header */}
                <Card sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <StorageIcon color="primary" sx={{ fontSize: 32 }} />
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                                Dynamic Procedures
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Esegui stored procedures MySQL in modo dinamico
                            </Typography>
                        </Box>
                    </Box>

                    {/* Selezione Procedura */}
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel>Seleziona Procedura</InputLabel>
                        <Select
                            value={selectedProc}
                            onChange={handleProcChange}
                            label="Seleziona Procedura"
                        >
                            <MenuItem value="">-- Seleziona una procedura --</MenuItem>
                            {procedures.map((proc) => (
                                <MenuItem key={proc.proc_name} value={proc.proc_name}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                        {getProcedureTypeIcon(proc)}
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography variant="body1">
                                                {proc.proc_name}
                                            </Typography>
                                            {proc.description && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {proc.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Bottoni azione */}
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Button
                            variant="contained"
                            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                            onClick={executeProcedure}
                            disabled={!selectedProc || loading}
                        >
                            {loading ? 'Esecuzione...' : 'Esegui Procedura'}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={loadProcedures}
                            disabled={loadingProcs}
                        >
                            Aggiorna Lista
                        </Button>
                        {/* Bottone Copia URL */}
                        {selectedProc && (
                            <Button
                                variant="outlined"
                                startIcon={copiedUrl ? <CheckIcon /> : <ContentCopyIcon />}
                                onClick={copyUrlToClipboard}
                                color={copiedUrl ? "success" : "secondary"}
                            >
                                {copiedUrl ? 'URL Copiato!' : 'Copia URL'}
                            </Button>
                        )}
                        {/* Bottone Copia JSON */}
                        {result && (
                            <Button
                                variant="outlined"
                                startIcon={copiedJson ? <CheckIcon /> : <ContentCopyIcon />}
                                onClick={copyJsonToClipboard}
                                color={copiedJson ? "success" : "primary"}
                            >
                                {copiedJson ? 'JSON Copiato!' : 'Copia JSON'}
                            </Button>
                        )}
                    </Box>
                </Card>

                {/* Configurazione Parametri */}
                {procConfig && (
                    <Card sx={{ mb: 3, borderRadius: 2 }}>
                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <SettingsIcon color="primary" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Parametri di Input
                                    </Typography>
                                    <Chip
                                        label={`${procConfig.input_params?.length || 0} parametri`}
                                        size="small"
                                        color="primary"
                                    />
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {!procConfig.input_params || procConfig.input_params.length === 0 ? (
                                    <Alert severity="info" icon={<InfoIcon />}>
                                        Questa procedura non richiede parametri di input.
                                    </Alert>
                                ) : (
                                    <Grid container spacing={3}>
                                        {procConfig.input_params.map((param) => (
                                            <Grid item xs={12} md={6} key={param.name}>
                                                <Box sx={{ mb: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                            {param.name}
                                                        </Typography>
                                                        <Chip
                                                            label={formatDataType(param.type)}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                        {param.required && (
                                                            <Chip
                                                                label="Obbligatorio"
                                                                size="small"
                                                                color="error"
                                                            />
                                                        )}
                                                    </Box>
                                                    {renderInputField(param)}
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    </Card>
                )}

                {/* Errori */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3 }} icon={<ErrorIcon />}>
                        {error}
                    </Alert>
                )}

                {/* Risultati */}
                {result && (
                    <Card sx={{ borderRadius: 2 }}>
                        <Accordion defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <DataObjectIcon color="success" />
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        Risultati
                                    </Typography>
                                    {/* Badge per il numero di record/tabelle */}
                                    {Array.isArray(result.data) ? (
                                        <Chip
                                            label={`${result.data.length} record`}
                                            size="small"
                                            color="success"
                                        />
                                    ) : isMultipleTemporaryTables(result.data) ? (
                                        <Chip
                                            label={`${Object.keys(result.data).filter(k => k.startsWith('tmp_') || k.startsWith('temp_')).length} tabelle`}
                                            size="small"
                                            color="success"
                                        />
                                    ) : null}
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {/* Informazioni procedura */}
                                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                Procedura: <code>{result.proc_name}</code>
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {Array.isArray(result.data) ? (
                                                    <>Record trovati: {result.data.length}</>
                                                ) : isMultipleTemporaryTables(result.data) ? (
                                                    <>Tabelle create: {Object.keys(result.data).filter(k => k.startsWith('tmp_') || k.startsWith('temp_')).length}</>
                                                ) : (
                                                    <>Record trovati: {result.count || 0}</>
                                                )}
                                            </Typography>
                                        </Grid>
                                        {result.timestampUTC && (
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="text.secondary">
                                                    Eseguita il: {new Date(result.timestampUTC).toLocaleString('it-IT')}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Box>

                                {/* URL della chiamata */}
                                {selectedProc && (
                                    <Accordion sx={{ mb: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <ContentCopyIcon color="secondary" />
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                    URL della Chiamata API
                                                </Typography>
                                            </Box>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box sx={{
                                                p: 2,
                                                bgcolor: '#f5f5f5',
                                                borderRadius: 1,
                                                border: '1px solid #e0e0e0',
                                                position: 'relative'
                                            }}>
                                                <Typography
                                                    variant="body2"
                                                    component="pre"
                                                    sx={{
                                                        fontFamily: 'monospace',
                                                        wordBreak: 'break-all',
                                                        whiteSpace: 'pre-wrap',
                                                        margin: 0,
                                                        fontSize: '0.85rem'
                                                    }}
                                                >
                                                    {buildApiUrl()}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={copyUrlToClipboard}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        bgcolor: 'white',
                                                        '&:hover': {
                                                            bgcolor: '#f0f0f0'
                                                        }
                                                    }}
                                                >
                                                    {copiedUrl ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                                                </IconButton>
                                            </Box>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                💡 <strong>Nota:</strong> Questo URL non include l'header di autenticazione JWT richiesto per la chiamata.
                                            </Typography>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Parametri utilizzati */}
                                {result.parameters_used && Object.keys(result.parameters_used).length > 0 && (
                                    <Accordion sx={{ mb: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                Parametri Utilizzati
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600 }}>Parametro</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Valore</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {Object.entries(result.parameters_used).map(([key, value]) => (
                                                            <TableRow key={key}>
                                                                <TableCell>{key}</TableCell>
                                                                <TableCell>
                                                                    <code>{value === null ? 'NULL' : String(value)}</code>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Output Parameters (se presenti) */}
                                {result.output_params && (
                                    <Accordion sx={{ mb: 2 }}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                Parametri di Output
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <TableContainer component={Paper} variant="outlined">
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ fontWeight: 600 }}>Parametro</TableCell>
                                                            <TableCell sx={{ fontWeight: 600 }}>Valore</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {Object.entries(result.output_params).map(([key, value]) => (
                                                            <TableRow key={key}>
                                                                <TableCell>{key}</TableCell>
                                                                <TableCell>
                                                                    <code>{value === null ? 'NULL' : String(value)}</code>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </AccordionDetails>
                                    </Accordion>
                                )}

                                {/* Sezione Dati - Gestisce sia singola tabella che multiple tabelle temporanee */}
                                <Accordion defaultExpanded>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            Dati
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        {renderResultData()}
                                    </AccordionDetails>
                                </Accordion>
                            </AccordionDetails>
                        </Accordion>
                    </Card>
                )}

                {/* Footer */}
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Dynamic Procedures - Sistema di esecuzione stored procedures
                    </Typography>
                </Box>
            </motion.div>
        </Box>
    );
};